import dayjs, { Dayjs } from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
// dayjs ships English only; without this import `.locale('pl')` silently falls
// back and the roster headers read "Mon, Tue" in a Polish UI.
import 'dayjs/locale/pl'
import { Shift, ShiftTemplate } from '../types/schedule.model'

dayjs.extend(isoWeek)

export const DATE_FORMAT = 'YYYY-MM-DD'

/**
 * The grid spans the full day. Bars routinely work into the small hours, so a
 * window that started at 06:00 simply hid those shifts.
 */
export const DAY_GRID_START_HOUR = 0
export const DAY_GRID_END_HOUR = 24

/**
 * Where the day and week calendars scroll to when they open.
 *
 * A full 24h day rarely fits on screen and landing on 00:00 shows empty night.
 * Fixed rather than derived from the earliest shift: a roster that starts at
 * 10:00 one week and 06:00 the next would open at a different place each time,
 * so the same hour is never in the same spot twice.
 */
export const DEFAULT_VISIBLE_HOUR = 6

export const MINUTES_IN_HOUR = 60

/**
 * What the shift modal's clocks list: every minute.
 *
 * Not the roster's drag snap, which is `DRAG_STEP_MINUTES` below. A quarter-hour
 * list is fine for dragging a block around, but a shift that really starts at
 * 10:20 has to be typeable.
 */
export const TIME_STEP_MINUTES = 1

/** Dragging a shift in the calendar snaps to half hours, like Teams. */
export const DRAG_STEP_MINUTES = 30

export const SHIFT_TEMPLATES: ShiftTemplate[] = [
  {
    id: 'morning',
    labelKey: 'SCHEDULE.TEMPLATES.MORNING',
    startMinutes: 10 * 60,
    endMinutes: 18 * 60
  },
  {
    id: 'evening',
    labelKey: 'SCHEDULE.TEMPLATES.EVENING',
    startMinutes: 16 * 60,
    endMinutes: 24 * 60
  },
  {
    id: 'full',
    labelKey: 'SCHEDULE.TEMPLATES.FULL_DAY',
    startMinutes: 10 * 60,
    endMinutes: 22 * 60
  }
]

/** Monday of the week the given day falls in. */
export const startOfWeek = (day: Dayjs): Dayjs => day.startOf('isoWeek')

/** The seven days of the week containing `day`, Monday first. */
export const weekDays = (day: Dayjs): Dayjs[] => {
  const monday = startOfWeek(day)
  return Array.from({ length: 7 }, (_, index) => monday.add(index, 'day'))
}

export const toDateKey = (day: Dayjs): string => day.format(DATE_FORMAT)

/**
 * Every Monday whose week overlaps `from`..`to`, inclusive at both ends.
 *
 * The shifts endpoint is addressed one week at a time, so any range the UI
 * reports over, a payroll month, an export period, has to be resolved into
 * the weeks that cover it before it can be fetched. Reporting a month off the
 * single week that happens to be on screen would silently understate it.
 */
export const weekStartsCovering = (from: Dayjs, to: Dayjs): string[] => {
  const last = startOfWeek(to)
  const weeks: string[] = []
  let week = startOfWeek(from)

  while (!week.isAfter(last, 'day')) {
    weeks.push(toDateKey(week))
    week = week.add(7, 'day')
  }

  return weeks
}

/**
 * 570 -> "09:30". An overnight end is stored past 1440, and a clock has no
 * "26:00", 1560 reads back as "02:00". 1440 itself stays "24:00", because a
 * shift ending exactly at midnight reads better as the end of its own day.
 */
export const formatMinutes = (minutes: number): string => {
  const rounded = Math.max(0, Math.round(minutes))
  const clamped =
    rounded > 24 * MINUTES_IN_HOUR ? rounded % (24 * MINUTES_IN_HOUR) : rounded
  const hours = Math.floor(clamped / MINUTES_IN_HOUR)
  const mins = clamped % MINUTES_IN_HOUR

  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
}

/*
  Mid-January, when no time zone changes its clocks. Today served until the
  day it did: the hours added past a clock change came out an hour off (22:00
  shown as 21:00 in Warsaw on 25 October), and the spring gap has no 02:30.
*/
const CLOCK_FACE_DAY = '2000-01-15'

/**
 * Minutes-from-midnight to a dayjs instant, for the MUI time picker. The day
 * itself is irrelevant, only the clock face is read back, so it anchors on a
 * fixed one rather than dragging the shift's date into the conversion.
 */
export const minutesToDayjs = (minutes: number | null): Dayjs | null =>
  minutes === null
    ? null
    : dayjs(CLOCK_FACE_DAY)
        .add(Math.floor(minutes / MINUTES_IN_HOUR), 'hour')
        .add(minutes % MINUTES_IN_HOUR, 'minute')

/** Clock face back to minutes from midnight; null for an unparsable value. */
export const dayjsToMinutes = (value: Dayjs | null): number | null =>
  value && value.isValid()
    ? value.hour() * MINUTES_IN_HOUR + value.minute()
    : null

/** Minutes in a full day, the offset an overnight end is expressed with. */
export const MINUTES_IN_DAY = 24 * MINUTES_IN_HOUR

/**
 * Resolves a typed end time against its start.
 *
 * A bar shift runs 16:00 → 02:00, and midnight reads as 00:00 on a clock. So an
 * end at or before the start means the next day, and is stored as minutes past
 * that day's midnight (02:00 → 1560). `endMinutes` is therefore NOT capped at
 * 1440; everything downstream measures `end - start`, which stays correct.
 */
export const resolveEndMinutes = (
  startMinutes: number,
  endMinutes: number
): number =>
  endMinutes <= startMinutes ? endMinutes + MINUTES_IN_DAY : endMinutes

export const crossesMidnight = (shift: Shift): boolean =>
  shift.endMinutes > MINUTES_IN_DAY

export const shiftDurationHours = (shift: Shift): number =>
  (shift.endMinutes - shift.startMinutes) / MINUTES_IN_HOUR

/**
 * A duration written the way a person says it, never as a fraction of an hour.
 *
 * `totalHours` returns a float, so a week whose shifts end on odd minutes lands
 * on things like 39.06666666666666, which is what the roster grid was printing.
 * Rounding it would not help: 39.1 godz. is both wrong and still unreadable.
 * The split happens on minutes, so nothing is lost.
 */
export const formatDuration = (
  hours: number,
  translate: (key: string, options?: Record<string, unknown>) => string
): string => {
  const totalMinutes = Math.round(hours * MINUTES_IN_HOUR)
  const wholeHours = Math.floor(totalMinutes / MINUTES_IN_HOUR)
  const minutes = totalMinutes % MINUTES_IN_HOUR

  if (!wholeHours) return translate('SCHEDULE.MINUTES_SHORT', { minutes })
  if (!minutes) return translate('SCHEDULE.HOURS_SHORT', { hours: wholeHours })

  return translate('SCHEDULE.HOURS_MINUTES', { hours: wholeHours, minutes })
}

/** Total scheduled hours in a list of shifts, for the row/day summaries. */
export const totalHours = (shifts: Shift[]): number =>
  shifts.reduce((sum, shift) => sum + shiftDurationHours(shift), 0)

/**
 * Minutes since a fixed epoch, so two shifts on different days compare
 * directly.
 *
 * Built from the date string through `Date.UTC` rather than from a local
 * `Date`: a shift is a wall-clock fact, and on the night the clocks change a
 * local day is 23 or 25 hours long, which would slide one of the two days by
 * an hour and either invent a clash or hide one.
 */
const absoluteMinutes = (date: string, minutes: number): number => {
  const [year, month, day] = date.split('-').map(Number)
  return Date.UTC(year, month - 1, day) / (60 * 1000) + minutes
}

/**
 * The shift `candidate` would double-book, or undefined when it is free.
 *
 * Nobody works two places at once, so a shift may not overlap another shift of
 * the SAME employee. Two people on at the same time is ordinary rostering and
 * is untouched, the calendar lays them out side by side on purpose.
 *
 * Compared in absolute minutes rather than within a day, because an overnight
 * shift stores its end past 1440: Monday 22:00–02:00 is 1320..1560 on Monday
 * and genuinely collides with Tuesday 00:30–08:00, which a same-day check
 * cannot see. Strict at both ends, so a shift finishing at 16:00 and one
 * starting at 16:00 are back to back rather than double-booked.
 */
export const findConflictingShift = (
  shifts: Shift[],
  candidate: Pick<Shift, 'userId' | 'date' | 'startMinutes' | 'endMinutes'> & {
    id?: string
  }
): Shift | undefined => {
  const start = absoluteMinutes(candidate.date, candidate.startMinutes)
  const end = absoluteMinutes(candidate.date, candidate.endMinutes)

  return shifts.find(shift => {
    if (shift.userId !== candidate.userId) return false
    // A shift never clashes with the version of itself being edited.
    if (candidate.id && shift.id === candidate.id) return false

    return (
      start < absoluteMinutes(shift.date, shift.endMinutes) &&
      absoluteMinutes(shift.date, shift.startMinutes) < end
    )
  })
}

/**
 * Lays overlapping shifts side by side in the day grid: every shift gets a
 * column index and the number of columns its cluster needs, so two people on
 * at the same time each get half the width instead of covering each other.
 */
export interface PositionedShift {
  shift: Shift
  column: number
  columns: number
}

export const positionOverlappingShifts = (
  shifts: Shift[]
): PositionedShift[] => {
  const sorted = [...shifts].sort(
    (a, b) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes
  )

  const positioned: PositionedShift[] = []
  let cluster: PositionedShift[] = []
  let clusterEnd = -1

  const flushCluster = () => {
    const columns =
      cluster.reduce((max, item) => Math.max(max, item.column), 0) + 1
    cluster.forEach(item => {
      positioned.push({ ...item, columns })
    })
    cluster = []
    clusterEnd = -1
  }

  sorted.forEach(shift => {
    if (cluster.length && shift.startMinutes >= clusterEnd) flushCluster()

    const taken = new Set(
      cluster
        .filter(item => item.shift.endMinutes > shift.startMinutes)
        .map(item => item.column)
    )

    let column = 0
    while (taken.has(column)) column += 1

    cluster.push({ shift, column, columns: 1 })
    clusterEnd = Math.max(clusterEnd, shift.endMinutes)
  })

  if (cluster.length) flushCluster()

  return positioned
}

export { dayjs }
